import { PayPalButtonOnApprove } from "@paypal/paypal-js"
import { CustomError } from "@paypal/paypal-server-sdk"
import { mapFormStateToApiData } from "src/checkout/helpers/mapFormState"
import { getCart, updateCartAddressesOrThrow } from "src/checkout/hooks/useCart"
import { checkoutContactStore } from "src/checkout/state/checkoutContactStore"
import { setExpressCheckoutErrorMessage, setExpressCheckoutPayPalRefs } from "src/checkout/state/checkoutExpressStore"
import { setStoreOrder } from "src/checkout/state/checkoutOrderStore"
import { CheckoutStep, setCheckoutStep } from "src/checkout/state/checkoutStageStore"
import { routes } from "src/config/routes"
import { wcCreateOrderOrThrow } from "src/data/woocommerce/wcStoreApi"
import { WCStoreCreateOrderRequestData } from "src/data/woocommerce/wcStoreTypes"
import {
  action_addOrderNote,
  action_authorizePayPalOrder,
  action_getPayPalOrder,
  action_setCustomIdOnPayPalOrder,
  action_setOrderStatus,
} from "src/integrations/paypal/actions"
import { paypalLogPrefix } from "src/integrations/paypal/constants"
import { mapPayPalOrderToCheckoutContactState } from "src/integrations/paypal/mapping"
import { trackAddPaymentInfo } from "src/tracking/events"
import { genericErrorMessage } from "src/utilities/errorMessages"
import { recordError } from "src/utilities/errorReporting"

export const payPalButtonOnApprove: PayPalButtonOnApprove = async (data, actions) => {
  let wcOrderId: number | undefined
  let succeeded = false

  try {
    console.debug(paypalLogPrefix, "onApprove", data, actions)

    // Get current PayPal order
    const paypalOrderId = data.orderID
    const paypalOrderResponse = await action_getPayPalOrder(paypalOrderId)
    console.debug(paypalLogPrefix, "getPayPalOrder response", paypalOrderResponse)
    let paypalOrder = paypalOrderResponse.result

    // We create the WooCommerce order before authorizing the payment so we can
    // set the order number on the PayPal order before authorizing it.  After
    // authorization, we can't change certain fields.
    const contactState = mapPayPalOrderToCheckoutContactState(paypalOrder)
    checkoutContactStore.setState(contactState)

    // Update WooCommerce cart addresses
    const wcAddressApiData = mapFormStateToApiData(contactState)
    await updateCartAddressesOrThrow(wcAddressApiData)

    // Create WooCommerce order from cart
    const cart = getCart()!
    const wcOrderApiData: WCStoreCreateOrderRequestData = {
      billing_address: cart.billing_address!,
      shipping_address: cart.shipping_address!,
      payment_method: "paypal",
    }
    const wcOrder = await wcCreateOrderOrThrow(wcOrderApiData)
    wcOrderId = wcOrder.order_id

    // Update local state and track order
    setStoreOrder(wcOrder)
    trackAddPaymentInfo(cart, wcOrder)

    // Reference PayPal order and WooCommerce order on each other
    await Promise.all([
      action_addOrderNote({
        orderId: wcOrderId,
        note: `PayPal Standard Checkout: Order ID: <u>${paypalOrderId}</u>`,
      }),
      action_setCustomIdOnPayPalOrder({
        paypalOrderId,
        wcOrderId: wcOrderId,
      }),
    ])

    // Authorize PayPal order
    const paypalAuthorizeResponse = await action_authorizePayPalOrder(paypalOrderId)
    console.debug(paypalLogPrefix, "authorizePayPalOrder response", paypalAuthorizeResponse)

    // Three cases to handle:
    //   (1) Recoverable INSTRUMENT_DECLINED -> Call actions.restart()
    //   (2) Non-recoverable UNKNOWN_ERROR -> Show failure message
    //   (3) Other non-recoverable errors -> Show failure message
    //   (4) Successful transaction -> Show confirmation page

    if (typeof paypalAuthorizeResponse === "string") {
      console.error(paypalLogPrefix, paypalAuthorizeResponse)

      // (1) Recoverable INSTRUMENT_DECLINED -> Call actions.restart()
      // https://developer.paypal.com/docs/checkout/standard/customize/handle-funding-failures/
      if (paypalAuthorizeResponse === "INSTRUMENT_DECLINED") {
        const errorMessage =
          "Die Zahlung wurde vom Zahlungsdienstleister abgelehnt. Bitte versuche es mit einer anderen Zahlungsmethode oder einem anderen Konto erneut."

        setExpressCheckoutErrorMessage(errorMessage)

        return actions.restart()
      }

      // (2) Non-recoverable UNKNOWN_ERROR -> Show failure message
      if (paypalAuthorizeResponse === "UNKNOWN_ERROR") {
        setExpressCheckoutErrorMessage(genericErrorMessage)
        throw new Error(paypalAuthorizeResponse)
      }
    }

    const paypalAuthorizeError = paypalAuthorizeResponse.result as CustomError["result"]
    const paypalAuthorizeErrorDetail = paypalAuthorizeError?.details?.[0]

    // (3) Other non-recoverable errors -> Show failure message
    if (paypalAuthorizeErrorDetail) {
      console.error(paypalLogPrefix, "Checkout failed", { errorDetail: paypalAuthorizeErrorDetail })
      throw new Error(`${paypalAuthorizeError.message}: ${paypalAuthorizeErrorDetail.description}`)
    }

    // (4) Successful transaction -> Update WooCommerce order and proceed to order completion page
    paypalOrder = paypalAuthorizeResponse.result
    const paypalTransactionId = paypalOrder.purchaseUnits?.[0]?.payments?.authorizations?.[0]?.id

    if (!paypalTransactionId) {
      console.error(paypalLogPrefix, "Order has no transactionId", paypalOrder)
      throw new Error(JSON.stringify(paypalOrder))
    }

    // Store PayPal data for the order completion page
    setExpressCheckoutPayPalRefs({ wcOrderId, paypalTransactionId })

    await action_addOrderNote({
      orderId: wcOrderId,
      note: `PayPal Standard Checkout: Transaction authorized: <a href="https://www.paypal.com/unifiedtransactions/details/payment/${paypalTransactionId}">${paypalTransactionId}</a>`,
    })

    // Display order completion screen
    succeeded = true
    window.location.href = routes.checkout
    setCheckoutStep(CheckoutStep.ExpressCheckoutCompletion)
  } catch (error) {
    recordError(paypalLogPrefix + " Failed to handle onApprove", error)
    setExpressCheckoutErrorMessage(genericErrorMessage)
  } finally {
    if (wcOrderId && !succeeded) {
      try {
        await action_setOrderStatus({ orderId: wcOrderId, status: "failed" })
      } catch (error) {
        recordError(paypalLogPrefix + " Failed to set order status to failed", error)
      }
    }
  }
}
